import React from "react"
import { Icon } from "~/react/ui/icon"
import { Skeleton } from "~/react/ui/skeleton"
import { Count } from "~/react/ui/count"
import { Card, CardTitle, CardTitleActions, CardContent } from "~/react/ui/card"
import { IssueItem } from "~/react/components/issue_item"

export type IssuesFeedAction = {
  url: string
  text: string
  htmlOptions?: Record<string, string>
} | null

export interface IssuesFeedIssueBase {
  title?: string
  description?: string
  severity?: string
  icon_name?: string
  css_class?: string
  id?: string | number
  action_url?: string
  action_text?: string
  action_html_options?: Record<string, string>
}

export interface IssuesFeedIssueGetters<T> {
  title: (issue: T) => string
  description: (issue: T) => string
  severity: (issue: T) => string
  icon: (issue: T) => string
  cssClass: (issue: T) => string
  key: (issue: T, index: number) => string
  action: (issue: T) => IssuesFeedAction
}

export interface IssuesFeedProps<T> {
  issues: T[]
  loading?: boolean
  title: string
  tooltip: string
  emptyStateTitle: string
  emptyStateDescription: string
  emptyStateIcon?: string
  getters?: Partial<IssuesFeedIssueGetters<T>>
}

const severityLabels: Record<string, string> = {
  critical: i18n.t("js.campaigns.issues.critical"),
  high: i18n.t("js.campaigns.issues.high"),
  medium: i18n.t("js.campaigns.issues.medium"),
  low: i18n.t("js.campaigns.issues.low"),
}

const getSeverityIcon = (severity: string): string => {
  switch (severity) {
    case "critical":
      return "exclamation-circle"
    case "high":
    case "medium":
      return "exclamation-triangle"
    case "low":
    default:
      return "info-circle"
  }
}

const getSeverityCssClass = (severity: string): string => {
  switch (severity) {
    case "critical":
      return "issues-feed-item--critical"
    case "high":
    case "medium":
      return "issues-feed-item--warning"
    case "low":
    default:
      return "issues-feed-item--notice"
  }
}

const LoadingSkeleton: React.FC = () => (
  <>
    <div className="issues-feed-item mt-2">
      <Skeleton
        size="freeform"
        style={{ width: "75%", height: "16px", borderRadius: "4px" }}
        className="mb-2"
      />
      <Skeleton
        size="freeform"
        style={{ width: "100%", height: "12px", borderRadius: "4px" }}
      />
    </div>
    <div className="issues-feed-item">
      <Skeleton
        size="freeform"
        style={{ width: "75%", height: "16px", borderRadius: "4px" }}
        className="mb-2"
      />
      <Skeleton
        size="freeform"
        style={{ width: "100%", height: "12px", borderRadius: "4px" }}
      />
    </div>
    <div className="issues-feed-item">
      <Skeleton
        size="freeform"
        style={{ width: "75%", height: "16px", borderRadius: "4px" }}
        className="mb-2"
      />
      <Skeleton
        size="freeform"
        style={{ width: "100%", height: "12px", borderRadius: "4px" }}
      />
    </div>
  </>
)


const EmptyState: React.FC<{
  title: string
  description: string
  icon?: string
}> = ({ title, description, icon = "envelope-circle-check" }) => (
  <div className="empty-state py-3">
    <Icon icon={icon} size="xl" className="empty-state__icon" />
    <div className="empty-state__content">
      <h3 className="empty-state__title text-sm-medium">{title}</h3>
      <p className="empty-state__description text-2xs">{description}</p>
    </div>
  </div>
)

export function IssuesFeed<T>({
  issues,
  loading = false,
  title,
  tooltip,
  emptyStateTitle,
  emptyStateDescription,
  emptyStateIcon,
  getters,
}: IssuesFeedProps<T>) {
  const getTitle =
    getters?.title ??
    ((issue: T) => {
      const base = issue as unknown as IssuesFeedIssueBase
      return base.title ?? base.description ?? ""
    })

  const getDescription =
    getters?.description ??
    ((issue: T) => {
      const base = issue as unknown as IssuesFeedIssueBase
      return base.description ?? ""
    })

  const getSeverity =
    getters?.severity ??
    ((issue: T) => {
      const base = issue as unknown as IssuesFeedIssueBase
      return base.severity ?? ""
    })

  const getIcon =
    getters?.icon ??
    ((issue: T) => {
      const base = issue as unknown as IssuesFeedIssueBase
      const iconName = base.icon_name
      if (iconName) return String(iconName).replace("fa-", "")
      return getSeverityIcon(getSeverity(issue))
    })

  const getCssClass =
    getters?.cssClass ??
    ((issue: T) => {
      const base = issue as unknown as IssuesFeedIssueBase
      const cssClass = base.css_class
      if (cssClass) return String(cssClass)
      return getSeverityCssClass(getSeverity(issue))
    })

  const getKey =
    getters?.key ??
    ((issue: T, index: number) => {
      const base = issue as unknown as IssuesFeedIssueBase
      return String(base.id ?? index)
    })

  const getAction =
    getters?.action ??
    ((issue: T) => {
      const base = issue as unknown as IssuesFeedIssueBase
      const url = base.action_url
      const text = base.action_text
      const htmlOptions = base.action_html_options
      if (!url || !text) return null
      return {
        url: String(url),
        text: String(text),
        htmlOptions: htmlOptions as Record<string, string> | undefined,
      }
    })

  return (
    <Card variant="widget" className="pb-0">
      <header className="widget-card__header">
        <CardTitle>
          {title}
          {!loading && issues.length > 0 ? (
            <Count number={issues.length.toString()} className="ml-2" />
          ) : (
            ""
          )}
          <CardTitleActions>
            <button
              className="question-circle"
              data-controller="tooltip"
              title={tooltip}
            >
              <Icon icon="question-circle" size="sm" />
            </button>
          </CardTitleActions>
        </CardTitle>
      </header>
      <CardContent>
        {loading ? (
          <LoadingSkeleton />
        ) : issues.length > 0 ? (
          <ul className="issues-feed">
            {issues.map((issue, index) => (
              <IssueItem
                key={getKey(issue, index)}
                title={getTitle(issue)}
                description={getDescription(issue)}
                action={getAction(issue)}
                severityIcon={getIcon(issue)}
                severityLabel={
                  severityLabels[getSeverity(issue)] || getSeverity(issue)
                }
                severityClass={getCssClass(issue)}
              />
            ))}
          </ul>
        ) : (
          <EmptyState
            title={emptyStateTitle}
            description={emptyStateDescription}
            icon={emptyStateIcon}
          />
        )}
      </CardContent>
    </Card>
  )
}

export default IssuesFeed
